import clsx from 'clsx';
import {
  ProportionChart,
  ProportionChartBar,
  ProportionChartListItem,
} from '../../UI';
import {
  RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_AFTER_TAX_INVESTMENTS_COLOR,
  RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_EDUCATION_INVESTMENTS_COLOR,
  RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_HEALTHCARE_INVESTMENTS_COLOR,
  RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_OTHER_INVESTMENTS_COLOR,
  RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_PRE_TAX_INVESTMENTS_COLOR,
} from '../constants';
import {
  UseRetirementAccountsSummaryProportionChartArgs,
  useRetirementAccountsSummaryProportionChart,
} from '../hooks';

interface RetirementAccountsSummaryProportionChartProps
  extends UseRetirementAccountsSummaryProportionChartArgs {
  className?: string;
}

export function RetirementAccountsSummaryProportionChart({
  className,
  ...useRetirementAccountsSummaryProportionChartArgs
}: RetirementAccountsSummaryProportionChartProps) {
  const { qtDetails, tUI } = useRetirementAccountsSummaryProportionChart(
    useRetirementAccountsSummaryProportionChartArgs
  );

  const hasEducation = !!qtDetails?.educationAccounts?.length;
  const hasHealthcare = !!qtDetails?.healthcareAccounts?.length;

  return (
    <ProportionChart
      className={clsx(
        className,
        '[&_.ProportionChart-TotalTitle]:!mix-blend-normal [&_.ProportionChart-TotalTitle]:!text-white'
      )}
      title={tUI('retirement-accounts-summary-proportion-chart-title')}
      totalTitle={tUI(
        'retirement-accounts-summary-proportion-chart-total-title'
      )}
      totalAmount={qtDetails?.totalRetirementAssets}
      barChartComponent={
        <ProportionChartBar
          data={[
            {
              id: 'totalTaxFree',
              value: qtDetails?.totalTaxFree,
              color:
                RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_AFTER_TAX_INVESTMENTS_COLOR,
            },
            {
              id: 'totalTaxDeferred',
              value: qtDetails?.totalTaxDeferred,
              color:
                RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_PRE_TAX_INVESTMENTS_COLOR,
            },
            {
              id: 'totalEducation',
              value: qtDetails?.totalEducation,
              color:
                RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_EDUCATION_INVESTMENTS_COLOR,
            },
            {
              id: 'totalHealthcare',
              value: qtDetails?.totalHealthcare,
              color:
                RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_HEALTHCARE_INVESTMENTS_COLOR,
            },
            {
              id: 'totalOther',
              value: qtDetails?.totalOther,
              color:
                RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_OTHER_INVESTMENTS_COLOR,
            },
          ]}
        />
      }
    >
      {([
        {
          titleKey: 'retirement-accounts-summary-proportion-chart-after-tax-investments',
          amount: qtDetails?.totalTaxFree,
          color: RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_AFTER_TAX_INVESTMENTS_COLOR,
          show: true,
        },
        {
          titleKey: 'retirement-accounts-summary-proportion-chart-pre-tax-investments',
          amount: qtDetails?.totalTaxDeferred,
          color: RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_PRE_TAX_INVESTMENTS_COLOR,
          show: true,
        },
        // Shown when the household holds such an account, matching how the sum
        // tables hide on empty rows — a $0 529 still gets an Education row. The
        // original three stay always-visible; they are the canonical breakdown.
        {
          titleKey: 'retirement-accounts-summary-proportion-chart-education-investments',
          amount: qtDetails?.totalEducation,
          color: RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_EDUCATION_INVESTMENTS_COLOR,
          show: hasEducation,
        },
        {
          titleKey: 'retirement-accounts-summary-proportion-chart-healthcare-investments',
          amount: qtDetails?.totalHealthcare,
          color: RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_HEALTHCARE_INVESTMENTS_COLOR,
          show: hasHealthcare,
        },
        {
          titleKey: 'retirement-accounts-summary-proportion-chart-other-investments',
          amount: qtDetails?.totalOther,
          color: RETIREMENT_ACCOUNTS_SUMMARY_PROPORTION_CHART_OTHER_INVESTMENTS_COLOR,
          show: true,
        },
      ] as const)
        .filter(({ show }) => show)
        .map(({ titleKey, amount, color }) => (
          <ProportionChartListItem key={titleKey} amount={amount} title={tUI(titleKey)} color={color} />
        ))}
    </ProportionChart>
  );
}
